import { useEffect, useState } from "react";
import operatorApiService from "../../../../services/operator-api.service";
import "../../MasterModal.scss";

interface EditBaseModalProps {
  isOpen: boolean;
  base: BaseInfo | null;
  onClose: () => void;
  onSuccess?: () => void;
  onUpdateBase: (updatedBase: BaseInfo) => void;
}

interface BaseInfo {
  base_id: number;
  base_name: string;
  zao_cloud_url: string;
  credential: string;
  map_latitude: number;
  map_longitude: number;
  map_zoom: number;
  monitor_error_cd: string;
}

interface ConfirmationRequestClass {
  confirmation_request_class_cd: string;
  confirmation_request_class_name: string;
}

const BaseEditModal = ({
  isOpen,
  base,
  onClose,
  onSuccess,
  onUpdateBase,
}: EditBaseModalProps) => {
  const [form, setForm] = useState<BaseInfo | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmationRequestClasses, setConfirmationRequestClasses] = useState<ConfirmationRequestClass[]>([]);

  const fetchConfirmationRequestClasses = async (baseId: number) => {
    const res = await operatorApiService.doGetRequestClasses({ baseId });
    const sortRes = res
      .map((entry: any) => ({
        ...entry,
        original_cd: entry.confirmation_request_class_cd,
        status: "original",
        isEditing: false,
        internalId: `existing-${entry.confirmation_request_class_cd}`,
      }))
      .sort(
        (a: any, b: any) =>
          Number(a.confirmation_request_class_cd) -
          Number(b.confirmation_request_class_cd)
      );
    setConfirmationRequestClasses(sortRes);
  };

  useEffect(() => {
    if (isOpen && base) {
      fetchConfirmationRequestClasses(base.base_id);
      setForm({ ...base });
    }
  }, [isOpen, base]);

  const handleChange = (field: keyof BaseInfo, value: string | number) => {
    if (form) {
      setForm({ ...form, [field]: value });
    }
  };

  const handleSubmit = async () => {
    if (!form) return;

    const {
      base_name,
      zao_cloud_url,
      credential,
      map_latitude,
      map_longitude,
      map_zoom,
      monitor_error_cd,
    } = form;

    if (
      base_name.trim() === "" ||
      zao_cloud_url.trim() === "" ||
      credential.trim() === "" ||
      map_latitude === null ||
      map_longitude === null ||
      map_zoom === null
    ) {
      setErrorMessage("空欄があります。");
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    if (
      base_name.trim().length > 6 ||
      zao_cloud_url.trim().length > 255 ||
      credential.trim().length > 255 ||
      monitor_error_cd.trim().length > 2
    ) {
      setErrorMessage("文字数が制限を超えています。");
      setTimeout(() => setErrorMessage(null), 3000);
      return;
    }

    try {
      await operatorApiService.editBase(form).then((response) => {
        if (response.status === "success") {
          setSuccessMessage("更新に成功しました。");
          onUpdateBase(form);
          onSuccess?.();
          setTimeout(() => {
            onClose();
            setSuccessMessage(null);
          }, 1000);
        }
      });
    } catch (err) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => {
        setErrorMessage(null);
      }, 3000);
    }
  };

  if (!isOpen || !form) return null;

  return (
    <div className="masterModal">
      <div className="modalOverlay">
        <div className="modalContent">
          <div className="modalHeader">
            <div className="modalTitle">拠点情報の編集</div>
          </div>
          <div className="modalBody">
            <div className="inputGroup">
              <label>拠点名</label>
              <input
                value={form.base_name}
                onChange={(e) => handleChange("base_name", e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>Zao Cloud URL</label>
              <input
                value={form.zao_cloud_url}
                onChange={(e) => handleChange("zao_cloud_url", e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>認証情報</label>
              <input
                value={form.credential}
                onChange={(e) => handleChange("credential", e.target.value)}
              />
            </div>
            <div className="inputGroup">
              <label>緯度</label>
              <input
                type="number"
                value={form.map_latitude}
                onChange={(e) =>
                  handleChange("map_latitude", Number(e.target.value))
                }
              />
            </div>
            <div className="inputGroup">
              <label>経度</label>
              <input
                type="number"
                value={form.map_longitude}
                onChange={(e) =>
                  handleChange("map_longitude", Number(e.target.value))
                }
              />
            </div>
            <div className="inputGroup">
              <label>地図ズーム</label>
              <input
                type="number"
                value={form.map_zoom}
                onChange={(e) =>
                  handleChange("map_zoom", Number(e.target.value))
                }
              />
            </div>
            <div className="inputGroup">
              <label>監視異常エラーコード</label>
              <select
                value={form.monitor_error_cd}
                onChange={(e) => handleChange("monitor_error_cd", e.target.value)}
              >
                {confirmationRequestClasses.map((crc) => (
                  <option
                    key={crc.confirmation_request_class_cd}
                    value={crc.confirmation_request_class_cd}
                  >
                    {`${crc.confirmation_request_class_cd}:${crc.confirmation_request_class_name}`}
                  </option>
                ))}
              </select>
            </div>
            {successMessage && (
              <div className="successMessage">{successMessage}</div>
            )}
            {errorMessage && <div className="errorMessage">{errorMessage}</div>}
          </div>
          <div className="modalFooter">
            <button className="backButton" onClick={onClose}>
              キャンセル
            </button>
            <button className="decisionButton" onClick={handleSubmit}>
              保存
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaseEditModal;
